"use client"

import { useEffect, useRef, useState } from "react"

/**
 * Hook to handle smooth scrolling to inputs on mobile devices
 * Ensures inputs are visible when focused and keyboard appears
 * Works with conditionally rendered elements
 */
export function useMobileInputScroll<T extends HTMLElement = HTMLInputElement>() {
  const inputRef = useRef<T>(null)
  const [elementReady, setElementReady] = useState(false)

  // Monitor when the element becomes available
  useEffect(() => {
    const checkElement = () => {
      if (inputRef.current && !elementReady) {
        setElementReady(true)
      } else if (!inputRef.current && elementReady) {
        setElementReady(false)
      }
    }

    // Check immediately and on every render
    checkElement()
    const interval = setInterval(checkElement, elementReady ? 1000 : 100)

    return () => clearInterval(interval)
  }, [elementReady])

  // Attach event listeners when element is ready
  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    let focusTimer: number | null = null
    let blurTimer: number | null = null

    const handleFocus = () => {
      // On mobile, ensure we scroll the input into view smoothly
      // Delay to allow keyboard to appear first
      if (focusTimer !== null) window.clearTimeout(focusTimer)
      focusTimer = window.setTimeout(() => {
        // Ensure element still exists before scrolling
        if (inputRef.current) {
          // Find the closest scrollable parent instead of scrolling the whole document
          const scrollContainer = findScrollableParent(inputRef.current)
          if (scrollContainer) {
            // Scroll within the container instead of the document
            const inputRect = inputRef.current.getBoundingClientRect()
            const containerRect = scrollContainer.getBoundingClientRect()
            const scrollTop = scrollContainer.scrollTop + (inputRect.top - containerRect.top) - (containerRect.height / 2)
            
            scrollContainer.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth'
            })
          } else {
            // Fallback to original behavior if no scrollable parent found
            inputRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            })
          }
        }
      }, 300)
    }

    const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
      let parent = element.parentElement
      while (parent) {
        // Check for our marked scroll container first
        if (parent.dataset.scrollContainer === 'true') {
          return parent
        }
        const style = window.getComputedStyle(parent)
        if (style.overflow === 'auto' || style.overflow === 'scroll' || 
            style.overflowY === 'auto' || style.overflowY === 'scroll') {
          return parent
        }
        parent = parent.parentElement
      }
      return null
    }

    const handleBlur = () => {
      // Minimal cleanup to avoid interfering with bottom sheet positioning
      // Just force a layout reflow without scrolling
      if (blurTimer !== null) window.clearTimeout(blurTimer)
      blurTimer = window.setTimeout(() => {
        document.body.style.height = '100vh'
        document.body.style.height = ''
      }, 100)
    }

    input.addEventListener('focus', handleFocus)
    input.addEventListener('blur', handleBlur)

    return () => {
      if (focusTimer !== null) window.clearTimeout(focusTimer)
      if (blurTimer !== null) window.clearTimeout(blurTimer)
      input.removeEventListener('focus', handleFocus)
      input.removeEventListener('blur', handleBlur)
    }
  }, [elementReady]) // Re-run when element becomes available

  return inputRef
}
